import { Checkbox } from '@/components/ui/checkbox';
import {
  canAttestOffPlatform,
  type DisplayRightsLaunchGate,
} from '@/components/display-rights/display-rights-launch-gate';
import {
  resolveDisplayRightsStatus,
  type DisplayRightsMetaState,
} from '@/components/display-rights/display-rights-status';
import { DisplayRightsTermsLine } from '@/components/display-rights/DisplayRightsTermsLine';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import type { DisplayRightsProposalSummary } from '@fluenceur/types';
import { CheckCircle, Hourglass } from 'iconoir-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface MentionBoostLaunchGateChecklistProps {
  gate: DisplayRightsLaunchGate;
  proposal: DisplayRightsProposalSummary | null;
  metaState: DisplayRightsMetaState;
  offPlatformAttestation: boolean;
  onOffPlatformAttestationChange: (value: boolean) => void;
}

interface GateRowProps {
  satisfied: boolean;
  label: string;
  detail: string;
  children?: ReactNode;
}

/** One validated side: its state, what it is, and where it stands. */
function GateRow({ satisfied, label, detail, children }: GateRowProps) {
  const Icon = satisfied ? CheckCircle : Hourglass;

  return (
    <HStack align="start" gap={2.5} px={3} py={2.5} data-testid="launch-gate-row">
      <Box color={satisfied ? 'green.600' : 'blue.600'} flexShrink={0} mt="1px">
        <Icon width={16} height={16} />
      </Box>
      <VStack align="start" gap={0.5} minW="0">
        <Text fontSize="xs" fontWeight="medium">
          {label}
        </Text>
        <Text fontSize="11px" color="fg.muted">
          {detail}
        </Text>
        {children}
      </VStack>
    </HStack>
  );
}

/**
 * The double validation, as the launch panel states it: Instagram granted the
 * permission, and the compensation is settled.
 *
 * Shown ONLY when compensation was declared. A boost nobody attached terms to
 * gets no checklist at all — an all-green list on a boost with nothing to check
 * would invent a ceremony where the product has none.
 *
 * The escape hatch appears only where it would actually unblock the launch: it
 * covers the money, never a permission Instagram withheld.
 */
export function MentionBoostLaunchGateChecklist({
  gate,
  proposal,
  metaState,
  offPlatformAttestation,
  onOffPlatformAttestationChange,
}: MentionBoostLaunchGateChecklistProps) {
  const { t } = useTranslation();

  if (!gate.applies) return null;

  const conditionsDetail = proposal
    ? t(`displayRights.status.${resolveDisplayRightsStatus(proposal)}`)
    : t('displayRights.status.none');

  return (
    <VStack
      align="stretch"
      gap={0}
      border="1px solid"
      borderColor="border.muted"
      borderRadius="md"
      data-testid="mention-boost-launch-gate"
    >
      <GateRow
        satisfied={gate.metaSatisfied}
        label={t('mentions.boost.launch.gate.metaLabel')}
        detail={t(`displayRights.meta.${metaState}`)}
      />
      <Box borderTop="1px solid" borderColor="border.muted">
        <GateRow
          satisfied={gate.conditionsSatisfied}
          label={t('mentions.boost.launch.gate.conditionsLabel')}
          detail={conditionsDetail}
        >
          <DisplayRightsTermsLine proposal={proposal} showVersion />
        </GateRow>
      </Box>

      {canAttestOffPlatform(gate) && (
        <Box borderTop="1px solid" borderColor="border.muted" px={3} py={2.5}>
          <Checkbox
            checked={offPlatformAttestation}
            onCheckedChange={(details) => onOffPlatformAttestationChange(details.checked === true)}
            size="sm"
            alignItems="flex-start"
            gap={2.5}
            data-testid="launch-gate-attestation"
          >
            <VStack align="start" gap={0.5}>
              <Text fontSize="xs" fontWeight="medium">
                {t('mentions.boost.launch.gate.attestationLabel')}
              </Text>
              <Text fontSize="11px" color="fg.muted">
                {t('mentions.boost.launch.gate.attestationHelp')}
              </Text>
            </VStack>
          </Checkbox>
        </Box>
      )}
    </VStack>
  );
}
